const COMPLETE_STATUSES = new Set(['complete', 'completed']);
const TERMINAL_NON_COMPLETION_STATUSES = new Set([
  'missed', 'expired', 'cancelled', 'canceled', 'skipped', 'stopped', 'rest', 'replaced',
]);

function dateKeyDayNumber(dateKey) {
  const [year, month, day] = String(dateKey || '').split('-').map(Number);
  if (!year || !month || !day) return null;
  return Math.floor(Date.UTC(year, month - 1, day) / 86400000);
}

function daysBetweenDateKeys(earlier, later) {
  const start = dateKeyDayNumber(earlier);
  const end = dateKeyDayNumber(later);
  if (start == null || end == null) return 0;
  return Math.max(0, end - start);
}

function signedDaysBetweenDateKeys(earlier, later) {
  const start = dateKeyDayNumber(earlier);
  const end = dateKeyDayNumber(later);
  if (start == null || end == null) return 0;
  return end - start;
}

function addProjection(map, dateKey, activity, timing) {
  if (!dateKey) return;
  if (!map[dateKey]) map[dateKey] = [];
  const exists = map[dateKey].some(item => String(item._id) === String(activity._id));
  if (exists) return;
  map[dateKey].push(timing ? { ...activity, _dojoTiming: timing } : activity);
}

export function projectDojoActivitiesByDate(activities = [], getDateKey, now = new Date()) {
  const map = {};
  const todayKey = getDateKey(now);
  const completedTravelReplacements = new Map();
  const replacedOriginalsByReplacementId = new Map();

  activities.forEach(activity => {
    if (activity?.status === 'replaced' && activity.travel_replacement_id) {
      replacedOriginalsByReplacementId.set(String(activity.travel_replacement_id), activity);
    }
    if (activity?.source?.compliance_role !== 'travel_replacement'
        || !COMPLETE_STATUSES.has(activity.status)
        || !activity.completed_at) return;
    completedTravelReplacements.set(String(activity._id), activity);
  });

  activities.forEach(activity => {
    const scheduledDateKey = getDateKey(activity.created_at || activity.due_at || activity.completed_at || now);
    const completed = COMPLETE_STATUSES.has(activity.status);
    const isCompletedTravelReplacement = completed
      && activity?.source?.compliance_role === 'travel_replacement'
      && activity.completed_at;
    if (isCompletedTravelReplacement) {
      const completedDateKey = getDateKey(activity.completed_at);
      const replacedOriginal = replacedOriginalsByReplacementId.get(String(activity._id));
      const authoritativeOriginal = replacedOriginal
        && String(replacedOriginal.user_id) === String(activity.user_id)
        && String(activity.source?.replaces_activity_id) === String(replacedOriginal._id)
        ? replacedOriginal
        : null;
      const originalScheduledDateKey = authoritativeOriginal
        ? getDateKey(authoritativeOriginal.created_at || authoritativeOriginal.due_at || authoritativeOriginal.completed_at || now)
        : null;
      if (originalScheduledDateKey === completedDateKey) return;
      addProjection(map, completedDateKey, activity, {
        scheduledDateKey: completedDateKey,
        completedDateKey,
        displayDateKey: completedDateKey,
        isCarryover: false,
        isDeferredCompletion: false,
        lateDays: 0,
      });
      return;
    }

    const linkedReplacement = activity.status === 'replaced' && activity.travel_replacement_id
      ? completedTravelReplacements.get(String(activity.travel_replacement_id))
      : null;
    const authoritativeLinkedReplacement = linkedReplacement
      && String(linkedReplacement.user_id) === String(activity.user_id)
      && String(linkedReplacement.source?.replaces_activity_id) === String(activity._id)
      ? linkedReplacement
      : null;
    const projectedActivity = authoritativeLinkedReplacement ? {
      ...activity,
      _dojoReplacementReference: {
        activity: authoritativeLinkedReplacement,
        completedDateKey: getDateKey(authoritativeLinkedReplacement.completed_at),
        relativeDays: signedDaysBetweenDateKeys(
          scheduledDateKey,
          getDateKey(authoritativeLinkedReplacement.completed_at)
        ),
      },
    } : activity;
    const terminalWithoutCompletion = TERMINAL_NON_COMPLETION_STATUSES.has(activity.status)
      || isDojoAgendaMissedActivity(activity, now);
    const completedDateKey = completed && activity.completed_at ? getDateKey(activity.completed_at) : null;
    const lateTargetKey = completedDateKey && completedDateKey > scheduledDateKey
      ? completedDateKey
      : (!completed && !terminalWithoutCompletion && scheduledDateKey < todayKey ? todayKey : null);
    const lateDays = lateTargetKey ? daysBetweenDateKeys(scheduledDateKey, lateTargetKey) : 0;

    addProjection(map, scheduledDateKey, projectedActivity, {
      scheduledDateKey,
      completedDateKey,
      displayDateKey: scheduledDateKey,
      isCarryover: false,
      isDeferredCompletion: Boolean(completedDateKey && completedDateKey > scheduledDateKey),
      lateDays,
    });

    if (lateTargetKey && lateTargetKey !== scheduledDateKey) {
      addProjection(map, lateTargetKey, projectedActivity, {
        scheduledDateKey,
        completedDateKey,
        displayDateKey: lateTargetKey,
        isCarryover: true,
        isDeferredCompletion: false,
        lateDays,
      });
    }
  });

  return map;
}

function previousDayLabel(days) {
  if (days === 1) return 'yesterday';
  return `${days} days earlier`;
}

function shortTimeAgo(value, now) {
  if (!value) return '';
  const diff = new Date(now).getTime() - new Date(value).getTime();
  if (!Number.isFinite(diff) || diff < 0) return '';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : '';
}

export function getPersonalLateWorkoutPresentation(activity, displayDateKey, getDateKey, now = new Date()) {
  if (activity?.type !== 'workout' || !COMPLETE_STATUSES.has(activity.status) || !activity.completed_at) return null;
  const scheduledDateKey = getDateKey(activity.created_at || activity.due_at || activity.completed_at);
  const completedDateKey = getDateKey(activity.completed_at);
  if (!scheduledDateKey || !completedDateKey || completedDateKey <= scheduledDateKey || displayDateKey !== completedDateKey) return null;
  const lateDays = daysBetweenDateKeys(scheduledDateKey, completedDateKey);
  const relative = shortTimeAgo(activity.completed_at, now);
  return {
    className: 'late-workout',
    detail: `${lateDays} day${lateDays === 1 ? '' : 's'} late${relative ? ` · ${relative}` : ''}`,
    lateDays,
  };
}

export function countsTowardDojoDayTotals(activity) {
  return activity?._dojoTiming?.isDeferredCompletion !== true;
}

export function isDojoAgendaMissedActivity(activity, now = new Date()) {
  if (['missed', 'expired'].includes(activity?.status)) return true;
  if (!['pending', 'ongoing', 'in_progress', 'started'].includes(activity?.status) || !activity?.due_at) return false;
  return new Date(activity.due_at).getTime() < new Date(now).getTime();
}

export function getDojoMatrixTimingPresentation(activity, { completed, ongoing = false, time } = {}) {
  const timing = activity?._dojoTiming;
  if (!timing || timing.lateDays <= 0) return null;

  if (timing.isDeferredCompletion) {
    return {
      className: 'deferred ghost',
      icon: '↗',
      detail: `completed ${timing.lateDays} day${timing.lateDays === 1 ? '' : 's'} later`,
    };
  }

  if (timing.isCarryover) {
    const prefix = `from ${previousDayLabel(timing.lateDays)}`;
    if (completed) {
      return {
        className: 'carryover late',
        icon: '✓',
        detail: `catch-up · ${timing.lateDays} day${timing.lateDays === 1 ? '' : 's'} late`,
      };
    }
    return {
      className: 'carryover late',
      icon: ongoing ? '●' : '↪',
      detail: `${prefix} · ${time || (ongoing ? 'in progress' : 'pending')}`,
    };
  }

  if (completed && timing.completedDateKey && timing.completedDateKey > timing.scheduledDateKey) {
    return {
      className: 'late',
      icon: '✓',
      detail: `${timing.lateDays} day${timing.lateDays === 1 ? '' : 's'} late${time ? ` · ${time}` : ''}`,
    };
  }

  return null;
}

export function dojoActivityRowKey(activity) {
  const type = activity?.type || 'other';
  const planEntryId = activity?.source?.activity_plan_entry_id;
  const base = planEntryId
    ? `${type}:plan-entry:${String(planEntryId)}`
    : type === 'media'
      ? `${type}:activity:${String(activity?._id)}`
      : `${type}:${(activity?.name || '').trim().toLowerCase()}`;
  const timing = activity?._dojoTiming;
  return timing?.isCarryover ? `${base}:carryover:${timing.scheduledDateKey}` : base;
}
